import React, { useState } from 'react';
import { X, Mail, Phone, User, MessageSquare, Send } from 'lucide-react';
import { createClient } from '@supabase/supabase-js';

// Contact email: dom@dm4u.ai — change to hello@dm4u.ai shortly
// Also referenced in Footer.tsx
const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

interface ContactFormProps {
  isOpen: boolean;
  onClose: () => void;
}

const ContactForm: React.FC<ContactFormProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    contactMethod: 'email'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form data
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      alert('Please fill in all required fields (Name, Email, and Message)');
      return;
    }

    setIsSubmitting(true);

    try {
      // Save to Supabase database
      const { error } = await supabase
        .from('contact_submissions')
        .insert([
          {
            name: formData.name,
            email: formData.email,
            phone: formData.phone || null,
            contact_method: formData.contactMethod,
            message: formData.message
          }
        ]);

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      // Show success message
      alert('Thank you! Your message has been sent successfully. We\'ll get back to you within 24 hours!');

      // Reset form and close modal
      setFormData({ name: '', email: '', phone: '', message: '', contactMethod: 'email' });
      onClose();

    } catch (error) {
      console.error('Error:', error);
      alert('Sorry, there was an error sending your message. Please try emailing us directly at dom@dm4u.ai or calling +447508300353');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4 bg-black/95 backdrop-blur-lg">
      <div className="relative w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 md:from-gray-900 md:via-gray-800 md:to-gray-900 from-purple-800/80 via-blue-800/60 to-gray-800/80 rounded-2xl sm:rounded-3xl border border-gray-600/50 md:border-gray-600/50 border-purple-400/50 shadow-2xl shadow-purple-600/30 md:shadow-purple-600/30 shadow-purple-500/40 animate-fade-in-up overflow-y-auto mobile-card-glow">
        {/* Gradient Border Effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600/30 via-blue-500/30 to-emerald-400/30 md:from-purple-600/30 md:via-blue-500/30 md:to-emerald-400/30 from-purple-500/40 via-blue-400/40 to-silver-400/40 rounded-2xl sm:rounded-3xl blur-lg -z-10"></div>
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 md:p-8 border-b border-gray-700/50 md:border-gray-700/50 border-purple-400/30 sticky top-0 bg-gray-900/90 md:bg-gray-900/90 bg-gradient-to-r from-purple-800/90 via-blue-800/70 to-gray-800/90 backdrop-blur-sm rounded-t-2xl sm:rounded-t-3xl">
          <div>
            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white md:text-white mobile-accent-purple font-robotic mb-1 sm:mb-2 mobile-text-glow">Get In Touch</h3>
            <p className="text-gray-400 md:text-gray-400 mobile-accent-blue font-tech text-xs sm:text-sm md:text-base">We'd love to hear from you and discuss your needs</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-xl hover:bg-red-500/20 hover:border-red-500/30 border border-gray-700/50 md:border-gray-700/50 border-purple-400/50 transition-all duration-300 hover:scale-110 flex-shrink-0 btn-mobile mobile-card-glow"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 md:text-gray-400 mobile-accent-silver hover:text-red-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 md:p-8 space-y-4 sm:space-y-6">
          {/* Name */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-200 md:text-gray-200 mobile-accent-white mb-2 font-tech">
              <User className="w-3 h-3 sm:w-4 sm:h-4 inline mr-2 mobile-accent-purple" />
              Full Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              className="w-full px-3 sm:px-4 py-3 bg-gray-800/60 md:bg-gray-800/60 bg-purple-800/40 border border-gray-600/50 md:border-gray-600/50 border-purple-400/50 rounded-xl text-white placeholder-gray-400 md:placeholder-gray-400 placeholder-purple-300 focus:border-blue-400/70 md:focus:border-blue-400/70 focus:border-purple-400/70 focus:outline-none focus:ring-2 focus:ring-blue-500/30 md:focus:ring-blue-500/30 focus:ring-purple-500/50 transition-all duration-300 hover:bg-gray-800/80 md:hover:bg-gray-800/80 hover:bg-purple-700/50 text-sm sm:text-base disabled:opacity-50 input-mobile mobile-card-glow"
              placeholder="Enter your full name"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-200 md:text-gray-200 mobile-accent-white mb-2 font-tech">
              <Mail className="w-3 h-3 sm:w-4 sm:h-4 inline mr-2 mobile-accent-blue" />
              Email Address *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              className="w-full px-3 sm:px-4 py-3 bg-gray-800/60 md:bg-gray-800/60 bg-blue-800/40 border border-gray-600/50 md:border-gray-600/50 border-blue-400/50 rounded-xl text-white placeholder-gray-400 md:placeholder-gray-400 placeholder-blue-300 focus:border-blue-400/70 md:focus:border-blue-400/70 focus:border-blue-400/70 focus:outline-none focus:ring-2 focus:ring-blue-500/30 md:focus:ring-blue-500/30 focus:ring-blue-500/50 transition-all duration-300 hover:bg-gray-800/80 md:hover:bg-gray-800/80 hover:bg-blue-700/50 text-sm sm:text-base disabled:opacity-50 input-mobile mobile-card-glow"
              placeholder="Enter your email address"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-200 mb-2 font-tech">
              <Phone className="w-3 h-3 sm:w-4 sm:h-4 inline mr-2" />
              Phone Number (Optional)
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              disabled={isSubmitting}
              className="w-full px-3 sm:px-4 py-3 bg-gray-800/60 border border-gray-600/50 rounded-xl text-white placeholder-gray-400 focus:border-blue-400/70 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all duration-300 hover:bg-gray-800/80 text-sm sm:text-base disabled:opacity-50 input-mobile"
              placeholder="Enter your phone number"
            />
          </div>

          {/* Contact Method */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-200 mb-2 font-tech">
              Preferred Contact Method
            </label>
            <select
              name="contactMethod"
              value={formData.contactMethod}
              onChange={handleChange}
              disabled={isSubmitting}
              className="w-full px-3 sm:px-4 py-3 bg-gray-800/60 border border-gray-600/50 rounded-xl text-white focus:border-blue-400/70 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all duration-300 hover:bg-gray-800/80 text-sm sm:text-base disabled:opacity-50 input-mobile"
            >
              <option value="email">Email</option>
              <option value="phone">Phone Call</option>
              <option value="both">Either Email or Phone</option>
            </select>
          </div>

          {/* Message */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-200 mb-2 font-tech">
              <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4 inline mr-2" />
              Message *
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              rows={3}
              className="w-full px-3 sm:px-4 py-3 bg-gray-800/60 border border-gray-600/50 rounded-xl text-white placeholder-gray-400 focus:border-blue-400/70 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all duration-300 hover:bg-gray-800/80 resize-none text-sm sm:text-base disabled:opacity-50 input-mobile"
              placeholder="Tell us about your business and how we can help..."
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full group relative px-6 sm:px-8 py-4 bg-gradient-to-r from-purple-600 via-blue-500 to-emerald-400 hover:from-purple-700 hover:via-blue-600 hover:to-emerald-500 rounded-xl text-white font-bold text-base sm:text-lg tracking-wide transition-all duration-300 hover:scale-[1.02] active:scale-95 hover:shadow-2xl hover:shadow-purple-600/40 glow-silver disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 cursor-pointer"
          >
            <span className="relative z-10 font-robotic flex items-center justify-center">
              <Send className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </span>
          </button>

          {/* Contact Info */}
          <div className="pt-4 border-t border-gray-700/50 text-center">
            <p className="text-xs sm:text-sm text-gray-300 md:text-gray-300 mobile-accent-silver mb-3 font-tech">Or reach out directly:</p>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-3 text-xs sm:text-sm">
              <a
                href="mailto:dom@dm4u.ai"
                className="flex items-center px-3 sm:px-4 py-2 bg-blue-500/10 rounded-lg text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 transition-all duration-300 font-tech hover:scale-105"
              >
                <Mail className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                dom@dm4u.ai
              </a>
              <a
                href="tel:+447508300353"
                className="flex items-center px-3 sm:px-4 py-2 bg-emerald-500/10 rounded-lg text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/20 transition-all duration-300 font-tech hover:scale-105"
              >
                <Phone className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                +447508300353
              </a>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactForm;